import { ApolloError, NetworkStatus, useQuery } from '@apollo/client';
import { pathEq, prop } from 'ramda';

import mergeConnectionResultsBy from 'common/graphql/mergeConnectionResultsBy';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { PageInfo } from 'types/pagination';

import { DirectDebitMandatesQueryDocument } from './directDebitMandatesQuery.gql';

export interface DirectDebitMandate {
  __typename?: 'DirectDebitMandate';
  directDebitMandateId: string;
  creditorName: string;
  isOldDebtorIban: boolean;
  isRevoked: boolean;
  label: string;
}

type DirectDebitMandateEdge = {
  node: DirectDebitMandate;
};

interface DirectDebitMandatesQueryData {
  viewer: {
    directDebitMandates: {
      edges: DirectDebitMandateEdge[];
      pageInfo: PageInfo;
    };
  };
}

interface DirectDebitMandatesResult {
  directDebitMandates: DirectDebitMandate[];
  error?: ApolloError;
  fetchMore: () => void;
  networkStatus: NetworkStatus;
  pageInfo?: PageInfo;
  refetch: () => void;
}

const MANDATES_PER_PAGE = 20;

const toList = (
  edges: DirectDebitMandateEdge[] | undefined,
): DirectDebitMandate[] => (edges ? edges.map(prop('node')) : []);

export const useDirectDebitMandatesQuery = ({
  isRevoked,
  skip = false,
}: {
  isRevoked: boolean;
  skip?: boolean;
}): DirectDebitMandatesResult => {
  const { companyProfileId } = useCompanyContext();
  const { data, error, fetchMore, networkStatus, refetch } =
    useQuery<DirectDebitMandatesQueryData>(DirectDebitMandatesQueryDocument, {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !companyProfileId || skip,
      variables: { companyProfileId, first: MANDATES_PER_PAGE, isRevoked },
    });

  const directDebitMandateEdges = data?.viewer?.directDebitMandates?.edges;
  const pageInfo = data?.viewer?.directDebitMandates?.pageInfo;

  const loadMore = (): void => {
    fetchMore({
      updateQuery: (
        previousResult,
        { fetchMoreResult },
      ): DirectDebitMandatesQueryData => {
        const isEqual = pathEq(
          previousResult,
          ['viewer', 'directDebitMandates', 'pageInfo'],
          fetchMoreResult,
        );
        return isEqual
          ? previousResult
          : mergeConnectionResultsBy('directDebitMandateId')(
              previousResult,
              fetchMoreResult,
            );
      },
      variables: {
        after: data?.viewer?.directDebitMandates?.pageInfo.endCursor,
      },
    });
  };

  return {
    directDebitMandates: toList(directDebitMandateEdges),
    error,
    fetchMore: loadMore,
    networkStatus,
    pageInfo,
    refetch,
  };
};

interface RefetchQueryArgs {
  companyProfileId: string;
  isRevoked: boolean;
}

export function refetchQueryDirectDebitMandates({
  companyProfileId,
  isRevoked,
}: RefetchQueryArgs) {
  return {
    query: DirectDebitMandatesQueryDocument,
    variables: {
      companyProfileId,
      first: MANDATES_PER_PAGE,
      isRevoked,
    },
  };
}
